/**
 * SpendChip — the live money readout for active-turn surfaces.
 *
 * Owner directive 2026-08-14 (Harbor Ledger long-run repro): any surface that
 * shows a running turn must also show spend, consistent with the rest of the
 * platform. Per-turn spend does not exist client-side mid-turn (usage lands on
 * the task doc only at completion, and the wallet debit is post-turn), so the
 * honest number is the account's LIVE wallet balance: server-authoritative,
 * already streamed by WalletSummaryContext's real-time Firestore listener, and
 * it visibly ticks down as metered tool calls land mid-turn. Fabricating a
 * per-turn estimate here would break the workUnits.ts reconciliation contract
 * ("displayed numbers reconcile with each other") — balance is the number that
 * always agrees with the Wallet page and the ⓘ usage popup.
 *
 * Renders the canonical pr0 idiom (workUnits.ts): formatWu + <Pr0Unit/>.
 * Fails closed on the same visibility gates as the ⓘ usage popup: signed-out
 * viewers and org members without usage visibility see nothing.
 */

import React from 'react';
import styled from 'styled-components';
import { useMaybeWalletSummary } from '../../context/WalletSummaryContext';
import { formatWu, usdToWu } from '../../config/workUnits';
import Pr0Unit from './Pr0Unit';
import { useViewerIsSignedOut } from '../GlobalChatSystem/guestUsagePrivacy';
import { useUsageDetailRestricted } from '../GlobalChatSystem/orgUsagePrivacy';

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: center;
  flex: 0 0 auto;
  margin-left: 8px;
  padding: 3px 10px;
  border: 1px solid rgba(198, 167, 94, 0.45);
  border-radius: 999px;
  background: #fff;
  color: var(--brand-gold-hover);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

export const SpendChip: React.FC<{ className?: string }> = ({ className }) => {
  const signedOut = useViewerIsSignedOut();
  const usageRestricted = useUsageDetailRestricted();
  const wallet = useMaybeWalletSummary();

  if (signedOut || usageRestricted) return null;
  if (!wallet || !wallet.hasUser || wallet.isLoading) return null;
  const usdBalance = wallet.summary?.usdBalance;
  if (typeof usdBalance !== 'number' || !Number.isFinite(usdBalance)) return null;

  return (
    <Chip className={className} title="wallet balance" data-testid="spend-chip">
      {formatWu(usdToWu(usdBalance))} <Pr0Unit />
    </Chip>
  );
};

export default SpendChip;
